import { useCallback, useEffect, useState } from 'react';

import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse
} from 'axios';

const handleError = (error: AxiosError) => {
  if (error.response) {
    const status = error.response.status;
    const statusText = error.response.statusText;
    const responseData: unknown = error.response.data;
    let message: string;
    if (typeof responseData === 'object' && responseData !== null && 'error' in responseData) {
      message = String((responseData as { error: unknown }).error);
      throw new Error(message);
    } else if (typeof responseData === 'string') {
      message = responseData;
    } else if (responseData) {
      message = JSON.stringify(responseData);
    } else {
      message = `HTTP ${status} ${statusText}`;
    }
    throw new Error(`Request failed with status ${status}: ${message}`);
  } else if (error.request) {
    throw new Error('Network error: Unable to reach the server');
  } else {
    throw new Error('Unknown error occurred');
  }
};

/**
 * Performs a GET request and returns the JSON response
 * @param url - The URL to make the request to
 * @param config - Optional axios configuration
 * @returns Promise that resolves to the JSON response data
 * @throws Error if the request fails or returns a non-200 status code
 */
export async function GetJSON<T = unknown>(
  url: string,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response: AxiosResponse<T> = await axios.get(url, config);
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleError(error);
    }
    // Re-throw other errors
    throw error instanceof Error ? error : new Error('Unknown error occurred');
  }
}

/**
 * Performs a POST request with JSON data and returns the JSON response
 * @param url - The URL to make the request to
 * @param params - The parameters to send in the request body
 * @param config - Optional axios configuration
 * @returns Promise that resolves to the JSON response data
 * @throws Error if the request fails or returns a non-200 status code
 */
export async function PostJSON<T = unknown, D = unknown>(
  url: string,
  params?: D,
  config?: AxiosRequestConfig
): Promise<T> {
  try {
    const response: AxiosResponse<T> = await axios.post(url, params, {
      ...config,
      headers: {
        'Content-Type': 'application/json',
        ...config?.headers,
      },
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      handleError(error);
    }
    // Re-throw other errors
    throw error instanceof Error ? error : new Error('Unknown error occurred');
  }
}

/**
 * React hook that performs GET requests and automatically refetches when parameters change
 * @param url - The URL to make the request to
 * @param config - Optional axios configuration
 * @param dependencies - Optional array of dependencies to trigger refetch when changed
 * @returns Object containing data, error, loading state, and refetch function
 */
export function useGetJSON<T = unknown>(
  url: string,
  config?: AxiosRequestConfig,
  dependencies?: unknown[]
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    if (!url) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await GetJSON<T>(url, config);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, config, ...(dependencies || [])]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  return { data, error, loading, refetch };
}

/**
 * React hook that performs POST requests with JSON data and automatically refetches when parameters change
 * @param url - The URL to make the request to
 * @param params - The parameters to send in the request body
 * @param config - Optional axios configuration
 * @returns Object containing data, error, loading state, and refetch function
 */
export function usePostJSON<T = unknown, D = unknown>(
  url: string,
  params?: D,
  dependencies?: unknown[],
  config?: AxiosRequestConfig
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchData = useCallback(async () => {
    if (!url) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const result = await PostJSON<T, D>(url, params, config);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error occurred'));
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, config, ...(dependencies || [])]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const refetch = useCallback(() => {
    void fetchData();
  }, [fetchData]);

  return { data, error, loading, refetch };
}
